import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/hooks/use-toast";
import {
  Upload,
  X,
  FileText,
  ExternalLink,
  Check,
  AlertCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Input } from "@shared/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { useLocation, useParams } from "react-router-dom";
import {
  useConsumerDetails,
  useDocumentType,
  useDocumentSubtype,
} from "@features/cx/consumerManagement/hooks";

import { useConfiguredDocumentTypes } from "@features/cx/shared/components/Useconfigureddocumenttypes";

// Types
interface Document {
  id: string;
  name: string;
  type: string;
  category: string;
  subCategory: string;
  categoryCode: string;
  subCategoryCode: string;
  size: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  url?: string;
  file?: File;
  needsReupload?: boolean;
  documentType?: string;
  documentSubtype?: string;
  consumerData?: any;
  referenceId?: string | null;
}

interface DocumentUploadComponentProps {
  remoteUtilityId: number;
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  consumerData?: any;
}

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
];


// Utility Functions
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const validateFile = (file: File): { isValid: boolean; error?: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File "${file.name}" exceeds the 5MB size limit`,
    };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: `File "${file.name}" is not a supported format. Only PDF, JPG, and PNG files are allowed`,
    };
  }

  return { isValid: true };
};

export function DocumentUploadComponent({
  remoteUtilityId,
  onDataChange,
  onValidationChange,
  consumerData,
}: DocumentUploadComponentProps) {
  const location = useLocation();
  const { consumerId } = useParams<{ consumerId: string }>();

  // State
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedSubTypes, setSelectedSubTypes] = useState<{ [key: string]: string }>({});
  const [referenceNumbers, setReferenceNumbers] = useState<{ [key: string]: string }>({});
  const [dragging, setDragging] = useState(false);
  const [shouldFetchDocuments, setShouldFetchDocuments] = useState(!!consumerId);

  // Use refs to track if we've already initialized to prevent duplicate notifications
  const initializedRef = useRef(false);
  const lastNotifiedPayloadRef = useRef<string>("");
  // API Hooks
  const {
    documentTypes: configuredDocumentTypes,
    isLoading: isConfigLoading,
    error: configError,
  } = useConfiguredDocumentTypes();

  const { data: consumerDetailsResponse, isLoading: isLoadingConsumerDetails } =
    useConsumerDetails(
      {
        consumer_id: consumerId?.toString() || "",
        remote_utility_id: String(remoteUtilityId),
        is_detail_info: true,
        is_document: true,
      },
      {
        enabled: !!consumerId && !!remoteUtilityId && shouldFetchDocuments,
      }
    );

  const { data: documentTypeData, isLoading: isDocumentTypeLoading } =
    useDocumentType({
      remote_utility_id: remoteUtilityId,
      config_level: "document_type",
    });

  // Trigger refetch when hash changes
  useEffect(() => {
    if (location.hash.includes("document-upload") && !!consumerId) {
      setShouldFetchDocuments((prev) => !prev);
    }
  }, [location.hash, !!consumerId]);

  // Initialize documents from API or create placeholders
  useEffect(() => {
    if (!configuredDocumentTypes.length) return;

    const consumerDetails = consumerDetailsResponse?.result;

    if (!!consumerId && consumerDetails?.document) {
      // Transform API documents
      const apiDocuments = consumerDetails.document.map((doc: any, index: number) => ({
        id: doc.id?.toString() || `api_doc_${index}`,
        name: doc.file?.split("/").pop() || `Document ${index + 1}`,
        type: doc.documentType || "",
        category: doc.documentType || "",
        subCategory: doc.documentSubtype || "",
        categoryCode: "",
        subCategoryCode: "",
        size: 0,
        progress: 100,
        status: "completed" as const,
        url: doc.file || "",
        file: undefined,
        needsReupload: false,
        documentType: doc.documentType || "",
        documentSubtype: doc.documentSubtype || "",
      }));

      // Create map of uploaded documents
      const uploadedDocsMap = new Map<string, typeof apiDocuments[0]>();
      apiDocuments.forEach((doc) => {
        if (doc.documentType) {
          uploadedDocsMap.set(doc.documentType, doc);
        }
      });

      // Merge with configured types
      const mergedDocuments: Document[] = configuredDocumentTypes.map((configType) => {
        const existingDoc = uploadedDocsMap.get(configType.name);

        if (existingDoc) {
          return {
            ...existingDoc,
            type: configType.id,
            categoryCode: configType.code,
          };
        }

        return {
          id: `placeholder_${configType.id}`,
          name: "",
          type: configType.id,
          category: configType.name,
          subCategory: "",
          categoryCode: configType.code,
          subCategoryCode: "",
          size: 0,
          progress: 0,
          status: "uploading" as const,
          url: "",
          file: undefined,
          needsReupload: false,
          documentType: configType.name,
          documentSubtype: "",
        };
      });

      setDocuments(mergedDocuments);

      // Set subtypes and reference numbers
      const subtypes: { [key: string]: string } = {};
      const initialRefNumbers: { [key: string]: string } = {};
      
      apiDocuments.forEach((apiDoc, index) => {
        const originalDoc = consumerDetails.document[index];
        const configType = configuredDocumentTypes.find(
          (ct) => ct.name === originalDoc.documentType
        );
        if (configType) {
          if (originalDoc.documentSubtype) {
            subtypes[configType.id] = originalDoc.documentSubtype;
          }
          const refId = originalDoc.referenceId || originalDoc.reference_id;
          if (refId) {
            initialRefNumbers[configType.id] = refId;
          }
        }
      });
      setSelectedSubTypes(subtypes);
      setReferenceNumbers(initialRefNumbers);
    } else {
      // Create mode - placeholders only
      const placeholderDocs: Document[] = configuredDocumentTypes.map((configType) => ({
        id: `placeholder_${configType.id}`,
        name: "",
        type: configType.id,
        category: configType.name,
        subCategory: "",
        categoryCode: configType.code,
        subCategoryCode: "",
        size: 0,
        progress: 0,
        status: "uploading" as const,
        url: "",
        file: undefined,
        needsReupload: false,
        documentType: configType.name,
        documentSubtype: "",
      }));

      setDocuments(placeholderDocs);
    }
  }, [!!consumerId, consumerDetailsResponse, configuredDocumentTypes]);

  // Memoize validation check to prevent unnecessary recalculations
  const isValid = useMemo(() => {
    const requiredTypes = configuredDocumentTypes.filter((doc) => doc.required);

    return requiredTypes.every((requiredType) => {
      const doc = documents.find((d) => d.type === requiredType.id);
      return doc && (doc.file || doc.url) && !doc.needsReupload;
    });
  }, [configuredDocumentTypes, documents]);

  // Memoize payload to prevent unnecessary recalculations
  const payload = useMemo(() => {
    const validDocuments = documents.filter(
      (doc) => (doc.file || doc.url) && !doc.needsReupload
    );

    // Separate new files from existing documents
    const newFiles = validDocuments.filter((doc) => doc.file);
    const existingDocuments = validDocuments.filter((doc) => doc.url && !doc.file);

    return {
      documents: documents.map((doc) => ({
        ...doc,
        referenceId: referenceNumbers[doc.type] || null,
      })),
      newFiles: newFiles.map((doc) => ({
        file: doc.file!,
        documentType: doc.category,
        documentSubtype: doc.subCategory,
        referenceId: referenceNumbers[doc.type] || null,
      })),
      existingDocuments: existingDocuments.map((doc) => ({
        id: doc.id,
        documentType: doc.documentType,
        documentSubtype: doc.documentSubtype,
        url: doc.url,
        referenceId: referenceNumbers[doc.type] || null,
      })),
      validFileCount: validDocuments.length,
      totalFileCount: documents.length,
    };
  }, [documents, referenceNumbers]);

  // Notify parent only when payload or validation actually changes
  useEffect(() => {
    // Serialize payload to check if it actually changed
    const payloadString = JSON.stringify({
      validFileCount: payload.validFileCount,
      totalFileCount: payload.totalFileCount,
      referenceNumbers,
      selectedSubTypes,
      isValid,
    });

    // Only notify if this is the first initialization or if the payload actually changed
    if (!initializedRef.current || payloadString !== lastNotifiedPayloadRef.current) {
      initializedRef.current = true;
      lastNotifiedPayloadRef.current = payloadString;

      if (onDataChange) {
        onDataChange(payload);
      }

      if (onValidationChange) {
        onValidationChange(isValid);
      }
    }
  }, [payload, isValid, onDataChange, onValidationChange]);

  // Handle file upload with stable callback
  const handleFileUpload = useCallback(
    (file: File, cardId: string, subtype: string) => {
      const validation = validateFile(file);

      if (!validation.isValid) {
        toast({
          title: "Error",
          description: validation.error,
          variant: "destructive",
        });
        return;
      }

      const configType = configuredDocumentTypes.find((ct) => ct.id === cardId);
      if (!configType) return;

      const documentId = `${file.name}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const newDocument: Document = {
        id: documentId,
        name: file.name,
        type: cardId,
        category: configType.name,
        subCategory: subtype,
        categoryCode: configType.code,
        subCategoryCode: "",
        size: file.size,
        progress: 100,
        status: "completed",
        file: file,
        needsReupload: false,
      };

      setDocuments((prevDocs) =>
        prevDocs.map((doc) => (doc.type === cardId ? newDocument : doc))
      );

      toast({
        title: "Success",
        description: `${file.name} has been added`,
      });
    },
    [configuredDocumentTypes]
  );

  // Remove document with stable callback
  const removeDocument = useCallback(
    (documentId: string) => {
      setDocuments((prevDocs) => {
        const docToRemove = prevDocs.find((doc) => doc.id === documentId);
        if (!docToRemove) return prevDocs;

        const configType = configuredDocumentTypes.find(
          (ct) => ct.id === docToRemove.type
        );
        if (!configType) return prevDocs;

        const placeholder: Document = {
          id: `placeholder_${configType.id}`,
          name: "",
          type: configType.id,
          category: configType.name,
          subCategory: "",
          categoryCode: configType.code,
          subCategoryCode: "",
          size: 0,
          progress: 0,
          status: "uploading" as const,
          url: "",
          file: undefined,
          needsReupload: false,
          documentType: configType.name,
          documentSubtype: "",
        };

        return prevDocs.map((doc) => (doc.id === documentId ? placeholder : doc));
      });

      setSelectedSubTypes((prev) => {
        const docToRemove = documents.find((doc) => doc.id === documentId);
        if (!docToRemove) return prev;

        const newSubTypes = { ...prev };
        delete newSubTypes[docToRemove.type];
        return newSubTypes;
      });
    },
    [configuredDocumentTypes, documents]
  );

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragging(false);
  }, []);

  // Render errors
  if (configError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>Failed to load configured document types</AlertDescription>
      </Alert>
    );
  }

  // Render loading state
  if (isConfigLoading || (!!consumerId && isLoadingConsumerDetails)) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Information Accordion */}
      <Accordion type="single" collapsible>
        <AccordionItem
          value="document-info"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-3" />
              <span className="font-medium text-blue-800">Document Information</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="text-blue-700 text-sm space-y-1 list-disc pl-5">
              <li>All documents must be clear and legible</li>
              <li>Accepted formats: JPG, PNG, PDF (max 5MB each)</li>
              <li>Upload at least one document for each required type</li>
              {!!consumerId && <li>You can replace existing documents or add new ones</li>}
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Document Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {configuredDocumentTypes.map((configType) => {
          const doc = documents.find((d) => d.type === configType.id);
          if (!doc) return null;

          const hasUploadedDocument = !!(doc.file || doc.url);
          const selectedSubtype = selectedSubTypes[configType.id];

          return (
            <DocumentCard
              key={configType.id}
              configType={configType}
              doc={doc}
              hasUploadedDocument={hasUploadedDocument}
              selectedSubtype={selectedSubtype}
              referenceNumber={referenceNumbers[configType.id] || ""}
              remoteUtilityId={remoteUtilityId}
              dragging={dragging}
              onSubtypeChange={(subtype) =>
                setSelectedSubTypes((prev) => ({
                  ...prev,
                  [configType.id]: subtype,
                }))
              }
              onReferenceNumberChange={(ref) =>
                setReferenceNumbers((prev) => ({
                  ...prev,
                  [configType.id]: ref,
                }))
              }
              onFileUpload={(file) =>
                handleFileUpload(file, configType.id, selectedSubtype)
              }
              onRemoveDocument={() => removeDocument(doc.id)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              isUploadDisabled={!selectedSubtype}
            />
          );
        })}
      </div>
    </div>
  );
}

// Document Card Component (Internal)
interface DocumentCardProps {
  configType: any;
  doc: Document;
  hasUploadedDocument: boolean;
  selectedSubtype: string;
  referenceNumber: string;
  remoteUtilityId: number;
  dragging: boolean;
  onSubtypeChange: (subtype: string) => void;
  onReferenceNumberChange: (ref: string) => void;
  onFileUpload: (file: File) => void;
  onRemoveDocument: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  isUploadDisabled: boolean;
}

function DocumentCard({
  configType,
  doc,
  hasUploadedDocument,
  selectedSubtype,
  referenceNumber,
  remoteUtilityId,
  dragging,
  onSubtypeChange,
  onReferenceNumberChange,
  onFileUpload,
  onRemoveDocument,
  onDragOver,
  onDragLeave,
  isUploadDisabled,
}: DocumentCardProps) {
  const [loadedSubtypes, setLoadedSubtypes] = useState<any[]>([]);

  // Fetch document subtypes
  const {
    data: documentSubtypeData,
    isLoading: isDocumentSubtypeLoading,
    error: documentSubtypeError,
  } = useDocumentSubtype(
    {
      remote_utility_id: remoteUtilityId,
      config_level: "document_subtype",
      code_list: configType.code,
    },
    {
      enabled: !!configType.code,
    }
  );

  // Store subtypes when received
  useEffect(() => {
    if (documentSubtypeData?.result) {
      setLoadedSubtypes(documentSubtypeData.result);
    }
  }, [documentSubtypeData]);

  // Handle file selection
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hasUploadedDocument) {
        toast({
          title: "Error",
          description:
            "Only one document allowed per document type. Please remove the existing document first.",
          variant: "destructive",
        });
        return;
      }

      if (!selectedSubtype) {
        toast({
          title: "Error",
          description: "Please select a document sub-type first",
          variant: "destructive",
        });
        return;
      }

      const files = e.target.files;
      if (files && files.length > 0) {
        onFileUpload(files[0]);
      }

      e.target.value = "";
    },
    [hasUploadedDocument, selectedSubtype, onFileUpload]
  );

  // Handle file drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      onDragLeave();

      if (hasUploadedDocument) {
        toast({
          title: "Error",
          description:
            "Only one document allowed per document type. Please remove the existing document first.",
          variant: "destructive",
        });
        return;
      }

      if (!selectedSubtype) {
        toast({
          title: "Error",
          description: "Please select a document sub-type first",
          variant: "destructive",
        });
        return;
      }

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFileUpload(files[0]);
      }
    },
    [hasUploadedDocument, selectedSubtype, onDragLeave, onFileUpload]
  );

  // Handle file download/viewing
  const openFileUrl = useCallback((url: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  }, []);

  const selectedSubtypeData = loadedSubtypes.find(s => s.name === selectedSubtype);
  const showReferenceNumber = selectedSubtypeData?.isReferenseNumber;

  return (
    <Card className="relative">
      <CardContent className="p-4">
        {/* Card Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">{configType.name}</h3>
            {configType.required ? (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                Required
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                Optional
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {/* Document Subtype Dropdown */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Document Sub Type
            </label>
            <Select
              value={selectedSubtype}
              onValueChange={onSubtypeChange}
              disabled={!configType.code || isDocumentSubtypeLoading}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isDocumentSubtypeLoading
                      ? "Loading..."
                      : selectedSubtype || "Select Sub Type"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {loadedSubtypes?.map((subtype: any) => (
                  <SelectItem key={subtype.id || subtype.code} value={subtype.name}>
                    {subtype.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isDocumentSubtypeLoading && (
              <p className="text-xs text-muted-foreground mt-1">Loading subtypes...</p>
            )}
            {!isDocumentSubtypeLoading &&
              loadedSubtypes.length === 0 &&
              configType.code &&
              !documentSubtypeError && (
                <p className="text-xs text-amber-600 mt-1">
                  No subtypes found for this document type
                </p>
              )}
            {documentSubtypeError && (
              <p className="text-xs text-red-500 mt-1">Failed to load subtypes</p>
            )}
            {hasUploadedDocument && selectedSubtype && (
              <p className="text-xs text-green-600 mt-1 flex items-center">
                <Check className="h-3 w-3 mr-1" />
                Currently selected: {selectedSubtype}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Reference Number (optional)
            </label>
            <Input
              value={referenceNumber}
              onChange={(e) => onReferenceNumberChange(e.target.value)}
              placeholder="Enter reference number"
            />
          </div>

          {/* File Upload Area or Uploaded Document */}
          {!hasUploadedDocument ? (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${dragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
                } ${isUploadDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:border-blue-400"
                }`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                onChange={handleFileChange}
                disabled={isUploadDisabled}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id={`file-upload-${configType.id}`}
              />
              <label
                htmlFor={`file-upload-${configType.id}`}
                className={`flex flex-col items-center ${isUploadDisabled ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
              >
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {isUploadDisabled
                    ? "Select sub type to upload"
                    : "Click to upload or drag and drop"}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  PDF, JPG, PNG (Max 5MB)
                </span>
                <span className="text-xs text-blue-500 mt-2">
                  Only one file allowed per document type
                </span>
              </label>
            </div>
          ) : (
            <div className="rounded-lg bg-gray-50 p-4 border border-gray-200">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-sm mb-2 flex items-center">
                  <Check className="h-4 w-4 mr-1 text-green-500" />
                  Uploaded Document
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRemoveDocument}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>

              <div
                className={`flex items-center mt-2 p-2 rounded border border-gray-200 bg-white ${doc.url ? "cursor-pointer hover:bg-blue-50" : ""
                  }`}
                onClick={() => doc.url && openFileUrl(doc.url)}
              >
                <FileText className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-sm flex-grow">{doc.name}</span>
                {doc.url && (
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>

              <div className="mt-2 flex items-center">
                <div className="text-xs text-gray-500">
                  Type: <span className="font-medium">{doc.category}</span>
                  <span className="mx-1">•</span>
                  Subtype: <span className="font-medium">{doc.subCategory}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}